import { useMemo } from "react";
import { useLang } from "@/contexts/LangContext";
import { Plus, Trash } from "@phosphor-icons/react";
import { parseDecimal } from "@/lib/patientUtils";

const CURRENCY_SYMBOL = { RON: "lei", EUR: "€", USD: "$", GBP: "£" };

export default function BillingPanel({ items = [], onChange, currency = "RON" }) {
  const { t } = useLang();

  function upd(i, k, v) {
    const rows = items.map((r, idx) => (idx === i ? { ...r, [k]: v } : r));
    onChange(rows);
  }
  function add() {
    onChange([...items, { description: "", category: "procedure", qty: 1, unit_price: 0, tax_pct: 19 }]);
  }
  function remove(i) {
    onChange(items.filter((_, idx) => idx !== i));
  }

  // `unit_price` is treated as the VAT-INCLUSIVE selling price the user actually
  // charges. We derive the net (VAT-excl) and the VAT amount from it, so the
  // gross the user typed never changes.
  const totals = useMemo(() => {
    let subtotal = 0; // net (VAT-excl)
    let tax = 0;
    let total = 0; // gross (VAT-incl)
    for (const it of items) {
      const gross = Number(it.qty || 0) * Number(it.unit_price || 0);
      const rate = Number(it.tax_pct || 0) / 100;
      const net = rate > 0 ? gross / (1 + rate) : gross;
      subtotal += net;
      tax += gross - net;
      total += gross;
    }
    return { subtotal, tax, total };
  }, [items]);

  const sym = CURRENCY_SYMBOL[currency] || currency;
  const fmt = (n) => `${Number(n || 0).toFixed(2)} ${sym}`;

  return (
    <div className="space-y-3" data-testid="billing-panel">
      <p className="text-xs text-pf-text-secondary">{t("proceduresAndProductsHint")}</p>
      <div className="border border-pf-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-pf-bg text-pf-text-secondary text-xs uppercase">
            <tr>
              <th className="text-left px-2 py-2 w-32">{t("itemCategory")}</th>
              <th className="text-left px-2 py-2">{t("itemDescription")}</th>
              <th className="text-left px-2 py-2 w-16">{t("qty")}</th>
              <th className="text-left px-2 py-2 w-24">{t("unitPrice")}</th>
              <th className="text-left px-2 py-2 w-16">{t("taxPct")}</th>
              <th className="text-right px-2 py-2 w-24">{t("lineTotal")}</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center text-pf-text-secondary text-xs py-4 italic">
                  —
                </td>
              </tr>
            )}
            {items.map((it, i) => {
              const line = Number(it.qty || 0) * Number(it.unit_price || 0);
              return (
                <tr key={i} className="border-t border-pf-border">
                  <td className="px-2 py-1">
                    <select
                      data-testid={`bill-cat-${i}`}
                      className="pf-input"
                      value={it.category || "procedure"}
                      onChange={(e) => upd(i, "category", e.target.value)}
                    >
                      <option value="procedure">{t("categoryProcedure")}</option>
                      <option value="product">{t("categoryProduct")}</option>
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <input
                      data-testid={`bill-desc-${i}`}
                      className="pf-input"
                      value={it.description || ""}
                      onChange={(e) => upd(i, "description", e.target.value)}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      data-testid={`bill-qty-${i}`}
                      type="text"
                      inputMode="decimal"
                      className="pf-input"
                      value={it.qty ?? 1}
                      onChange={(e) => upd(i, "qty", e.target.value === "" ? 0 : parseDecimal(e.target.value))}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      data-testid={`bill-price-${i}`}
                      type="text"
                      inputMode="decimal"
                      className="pf-input"
                      value={it.unit_price ?? 0}
                      onChange={(e) => upd(i, "unit_price", e.target.value === "" ? 0 : parseDecimal(e.target.value))}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      data-testid={`bill-tax-${i}`}
                      type="text"
                      inputMode="decimal"
                      className="pf-input"
                      value={it.tax_pct ?? 0}
                      onChange={(e) => upd(i, "tax_pct", e.target.value === "" ? 0 : parseDecimal(e.target.value))}
                    />
                  </td>
                  <td className="px-2 py-1 text-right font-medium">{fmt(line)}</td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      className="p-1 text-pf-text-secondary hover:text-pf-danger"
                    >
                      <Trash size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button
          type="button"
          onClick={add}
          data-testid="bill-add-row"
          className="inline-flex items-center gap-1 text-sm text-pf-primary hover:underline"
        >
          <Plus size={14} weight="bold" /> {t("addRow")}
        </button>
        <div className="text-sm text-right space-y-0.5">
          <div>
            {t("subtotal")}: <span className="font-semibold" data-testid="bill-subtotal">{fmt(totals.subtotal)}</span>
          </div>
          <div>
            {t("tax")}: <span className="font-semibold">{fmt(totals.tax)}</span>
          </div>
          <div className="text-lg font-display font-bold text-pf-primary">
            {t("consultationTotal")}: <span data-testid="bill-total">{fmt(totals.total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
